import * as Ariakit from '@ariakit/react/combobox'
import { CaretDownIcon, CheckIcon, MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react'
import * as RadixPopover from '@radix-ui/react-popover'
import { useDebouncedValue } from '@tanstack/react-pacer'
import classNames from 'classnames'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetInfiniteRepositoryBranches } from '../../utils/api-client/api-hooks'
import { type BranchResponse, type Tool } from '../../utils/api-client/api-schemas'
import { formatCompactAge, formatDate } from '../../utils/date-utils'
import { pluralize } from '../../utils/text-utils'
import { TooltipWithHover } from '../tooltip'
import * as styles from './branch-selector.css'
import {
  type BranchSections,
  buildBranchSections,
  keepPreviousBranchesOf,
  LATEST_SCAN_EMBED,
  useGetKnownRepositoryBranches,
  useRecentlyVisitedBranches,
} from './branch-selector.hooks'

const SEARCH_DEBOUNCE_MS = 250

/** How close to the bottom of the list, in pixels, scrolling has to get before the next page loads. */
const LOAD_MORE_THRESHOLD_PX = 48

// #region Wrapper

export type BranchSelectorProps = {
  repositoryId: string
  /** The repository's `branches` link, which the lists are fetched from. */
  branchesHref?: string
  tool: Tool
  /**
   * The branch named in the page's URL. It is looked up by name together with the default and
   * recently visited branches, in the request the page resolves its branch from, so passing it keeps
   * the two on one request.
   */
  urlBranch?: string
  selectedBranch: string
  onSelectBranch: (branch: string) => unknown
  /** For the repository-wide views, which have no branch dimension. Kept visible, but inert. */
  isDisabled?: boolean
  /** Why it is inert, shown in a tooltip on hover and focus. */
  disabledReason?: string
}

export const BranchSelector: React.FC<BranchSelectorProps> = ({
  repositoryId,
  branchesHref,
  tool,
  urlBranch,
  selectedBranch,
  onSelectBranch,
  isDisabled,
  disabledReason,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchValue.trim(), { wait: SEARCH_DEBOUNCE_MS })
  const { recentlyVisited, recordVisit } = useRecentlyVisitedBranches({ repositoryId })
  const { sectionBranches } = useGetKnownRepositoryBranches({ repositoryId, tool, urlBranch })

  // Every row shows how long ago its branch's latest scan was imported, and another search keeps the
  // previous results on screen until its own arrive.
  const listOptions = {
    branchesHref,
    tool,
    embed: LATEST_SCAN_EMBED,
    placeholderData: keepPreviousBranchesOf({ branchesHref, tool }),
  }
  const allBranches = useGetInfiniteRepositoryBranches({ ...listOptions, enabled: isOpen })
  const searchedBranches = useGetInfiniteRepositoryBranches({
    ...listOptions,
    search: debouncedSearch,
    enabled: isOpen && debouncedSearch !== '',
  })

  const sections = useMemo(
    () =>
      allBranches.data && sectionBranches
        ? buildBranchSections({
            knownBranches: sectionBranches,
            recentlyVisited,
            branches: allBranches.data.pages.flatMap(page => page._embedded.items),
          })
        : undefined,
    [allBranches.data, sectionBranches, recentlyVisited]
  )

  const isSearching = searchValue.trim() !== ''
  const activeList = isSearching ? searchedBranches : allBranches
  const searchResults = useMemo(
    () =>
      // Until the debounced search catches up with the input, the previous search's results would
      // be shown under a count that claims they match what is typed.
      isSearching &&
      debouncedSearch === searchValue.trim() &&
      searchedBranches.data &&
      !searchedBranches.isPlaceholderData
        ? {
            items: searchedBranches.data.pages.flatMap(page => page._embedded.items),
            total: searchedBranches.data.pages[0]?.total ?? 0,
          }
        : undefined,
    [isSearching, debouncedSearch, searchValue, searchedBranches.data, searchedBranches.isPlaceholderData]
  )

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setSearchValue('')
    }
  }, [])

  const handleSelectBranch = useCallback(
    (branch: string) => {
      recordVisit(branch)
      onSelectBranch(branch)
      handleOpenChange(false)
    },
    [recordVisit, onSelectBranch, handleOpenChange]
  )

  return (
    <BranchSelectorPure
      selectedBranch={selectedBranch}
      onSelectBranch={handleSelectBranch}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      searchValue={searchValue}
      onSearchValueChange={setSearchValue}
      sections={sections}
      searchResults={searchResults}
      hasNextPage={activeList.hasNextPage}
      isFetchingNextPage={activeList.isFetchingNextPage}
      onLoadMore={activeList.fetchNextPage}
      isError={allBranches.isError || (isSearching && searchedBranches.isError)}
      isDisabled={isDisabled}
      disabledReason={disabledReason}
    />
  )
}

// #endregion

// #region Pure Component

export type BranchSelectorPureProps = {
  selectedBranch: string
  onSelectBranch: (branch: string) => unknown
  isOpen: boolean
  onOpenChange: (open: boolean) => unknown
  searchValue: string
  onSearchValueChange: (value: string) => unknown
  /** The unsearched sections; `undefined` while they load. */
  sections?: BranchSections
  /** The matches for `searchValue`; `undefined` while they load. */
  searchResults?: { items: BranchResponse[]; total: number }
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onLoadMore?: () => unknown
  isError?: boolean
  isDisabled?: boolean
  disabledReason?: string
  /** The reference time for the "Latest import" ages. Defaults to the current time. */
  now?: string
}

export const BranchSelectorPure: React.FC<BranchSelectorPureProps> = ({
  selectedBranch,
  onSelectBranch,
  isOpen,
  onOpenChange,
  searchValue,
  onSearchValueChange,
  sections,
  searchResults,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  isError = false,
  isDisabled = false,
  disabledReason,
  now,
}) => {
  const triggerLabel = `Branch: ${selectedBranch}`
  const triggerContent = (
    <>
      <span className={styles.badge} title={selectedBranch}>
        {selectedBranch}
      </span>
      <CaretDownIcon className={styles.caret} size={16} weight="bold" aria-hidden />
    </>
  )

  if (isDisabled) {
    // `aria-disabled` rather than `disabled`, like the tool selector: a disabled button leaves the tab
    // order, so a keyboard user would never reach the explanation for why it is inert.
    const trigger = (
      <button
        type="button"
        aria-disabled
        aria-label={triggerLabel}
        className={classNames(styles.trigger, styles.triggerDisabled)}
      >
        {triggerContent}
      </button>
    )

    return disabledReason ? (
      <TooltipWithHover trigger={trigger} side="bottom" size="small" maxWidth={260}>
        {disabledReason}
      </TooltipWithHover>
    ) : (
      trigger
    )
  }

  const isSearching = searchValue.trim() !== ''

  return (
    <RadixPopover.Root open={isOpen} onOpenChange={onOpenChange}>
      <RadixPopover.Trigger className={styles.trigger} aria-label={triggerLabel}>
        {triggerContent}
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content className={styles.content} align="start" aria-label="Select a branch">
          <Ariakit.ComboboxProvider
            open={isOpen}
            // The list lives inside the popover, so the popover owns visibility: closing the
            // combobox (Escape, picking an item) closes the popover, and it never opens on its own.
            setOpen={open => {
              if (!open) {
                onOpenChange(false)
              }
            }}
            inputValue={searchValue}
            setInputValue={onSearchValueChange}
            selectedValue={selectedBranch}
            resetValueOnHide={false}
          >
            <div className={styles.searchContainer}>
              <MagnifyingGlassIcon size={18} aria-hidden />
              <Ariakit.Combobox
                className={styles.searchInput}
                placeholder="Search branches…"
                aria-label="Search branches"
                autoSelect
              />
              {searchValue !== '' && (
                <button
                  type="button"
                  className={styles.clearSearch}
                  aria-label="Clear search"
                  onClick={() => onSearchValueChange('')}
                >
                  <XIcon size={12} weight="bold" aria-hidden />
                </button>
              )}
            </div>
            {isSearching && <SearchResultsHeader searchResults={searchResults} />}
            <BranchList
              selectedBranch={selectedBranch}
              onSelectBranch={onSelectBranch}
              isSearching={isSearching}
              searchValue={searchValue}
              sections={sections}
              searchResults={searchResults}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={onLoadMore}
              isError={isError}
              now={now}
            />
          </Ariakit.ComboboxProvider>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  )
}

/** The match count, with the "Latest import" label over the time column. */
const SearchResultsHeader: React.FC<{ searchResults?: BranchSelectorPureProps['searchResults'] }> = ({
  searchResults,
}) => (
  <div className={styles.listHeader}>
    <span role="status">
      {searchResults
        ? `${searchResults.total} matching ${pluralize(searchResults.total, 'branch', 'branches')}`
        : 'Searching…'}
    </span>
    <span aria-hidden>Latest import</span>
  </div>
)

type BranchListProps = Pick<
  BranchSelectorPureProps,
  'selectedBranch' | 'onSelectBranch' | 'sections' | 'searchResults' | 'onLoadMore' | 'now'
> & {
  isSearching: boolean
  searchValue: string
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isError: boolean
}

const BranchList: React.FC<BranchListProps> = ({
  selectedBranch,
  onSelectBranch,
  isSearching,
  searchValue,
  sections,
  searchResults,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  isError,
  now,
}) => {
  const listRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
      if (hasNextPage && !isFetchingNextPage && scrollTop + clientHeight >= scrollHeight - LOAD_MORE_THRESHOLD_PX) {
        onLoadMore?.()
      }
    },
    [hasNextPage, isFetchingNextPage, onLoadMore]
  )

  const groups: BranchGroup[] | undefined = isSearching
    ? searchResults && [{ label: undefined, branches: searchResults.items, showsAge: true, showsDivider: false }]
    : sections && [
        // The default branch is the page's starting point, not a recent piece of work, so it has no time.
        {
          label: 'Default',
          branches: sections.defaultBranch ? [sections.defaultBranch] : [],
          showsAge: false,
          showsDivider: false,
        },
        { label: 'Recently visited', branches: sections.recentBranches, showsAge: true, showsDivider: false },
        // Set apart from the sections above, which are the reader's own starting points.
        { label: 'Other branches', branches: sections.otherBranches, showsAge: true, showsDivider: true },
      ]
  const visibleGroups = groups?.filter(group => group.branches.length > 0)
  const shownCount = visibleGroups?.reduce((count, group) => count + group.branches.length, 0) ?? 0

  useEffect(() => {
    const list = listRef.current
    // A page shorter than the list leaves nothing to scroll, so no scroll event would ever ask for the
    // next one. An unmeasured list (height 0, e.g. not laid out yet) says nothing either way. A failed
    // page stops it: `hasNextPage` still reflects the last page that loaded, so without the error check
    // every failure would ask again for as long as the list is open.
    if (
      list &&
      list.clientHeight > 0 &&
      list.scrollHeight <= list.clientHeight &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isError
    ) {
      onLoadMore?.()
    }
  }, [shownCount, hasNextPage, isFetchingNextPage, isError, onLoadMore])

  return (
    <Ariakit.ComboboxList
      ref={listRef}
      alwaysVisible
      className={styles.list}
      aria-label="Branches"
      onScroll={handleScroll}
    >
      {isError && <p className={styles.status}>Could not load branches.</p>}
      {!isError && !visibleGroups && <p className={styles.status}>Loading branches…</p>}
      {!isError && visibleGroups?.length === 0 && (
        <p className={styles.status}>{isSearching ? `No branches match “${searchValue.trim()}”` : 'No branches'}</p>
      )}
      {visibleGroups?.map((group, index) => (
        <Fragment key={group.label ?? 'results'}>
          {group.label ? (
            <Ariakit.ComboboxGroup
              className={classNames(styles.group, { [styles.groupWithDivider]: index > 0 && group.showsDivider })}
            >
              <div className={styles.groupHeader}>
                <Ariakit.ComboboxGroupLabel className={styles.groupLabel}>{group.label}</Ariakit.ComboboxGroupLabel>
                {/* Labels the time column once, on whichever section comes first. */}
                {index === 0 && (
                  <span className={styles.timeColumnLabel} aria-hidden>
                    Latest import
                  </span>
                )}
              </div>
              <BranchItems group={group} selectedBranch={selectedBranch} onSelectBranch={onSelectBranch} now={now} />
            </Ariakit.ComboboxGroup>
          ) : (
            <div className={styles.group}>
              <BranchItems group={group} selectedBranch={selectedBranch} onSelectBranch={onSelectBranch} now={now} />
            </div>
          )}
        </Fragment>
      ))}
      {isFetchingNextPage && <p className={styles.status}>Loading more branches…</p>}
    </Ariakit.ComboboxList>
  )
}

type BranchGroup = {
  /** `undefined` for search results, which are one unlabelled list. */
  label?: string
  branches: BranchResponse[]
  /** Whether the rows show how long ago each branch's latest scan was imported. */
  showsAge: boolean
  /** Whether a rule separates the section from the ones above it. */
  showsDivider: boolean
}

const BranchItems: React.FC<
  { group: BranchGroup } & Pick<BranchListProps, 'selectedBranch' | 'onSelectBranch' | 'now'>
> = ({ group, selectedBranch, onSelectBranch, now }) =>
  group.branches.map(branch => (
    <BranchItem
      key={branch.name}
      branch={branch}
      isSelected={branch.name === selectedBranch}
      onSelect={onSelectBranch}
      showsAge={group.showsAge}
      now={now}
    />
  ))

const BranchItem: React.FC<{
  branch: BranchResponse
  isSelected: boolean
  onSelect: (branch: string) => unknown
  /** Whether the row shows how long ago the branch's latest scan was imported. */
  showsAge: boolean
  now?: string
}> = ({ branch, isSelected, onSelect, showsAge, now }) => {
  const importedAt = showsAge ? branch._embedded?.['latest-scan']?.imported_at : undefined
  const check = isSelected && (
    <CheckIcon className={styles.itemCheck} size={16} weight="bold" role="img" aria-label="Selected" />
  )

  return (
    <Ariakit.ComboboxItem
      className={styles.item}
      value={branch.name}
      setValueOnClick={false}
      onClick={() => onSelect(branch.name)}
    >
      <span className={styles.itemName} title={branch.name}>
        {branch.name}
      </span>
      {importedAt ? (
        <>
          <span aria-hidden={!isSelected} className={styles.itemCheckSlot}>
            {check}
          </span>
          <time
            className={styles.itemAge}
            dateTime={importedAt}
            title={`Latest import ${formatDate({ date: importedAt, type: 'mm/dd/yyyy-hh:mm' })}`}
          >
            {formatCompactAge(importedAt, now)}
          </time>
        </>
      ) : (
        // A row without a time (the default branch) shows its check mark in the time column, as in the design.
        <span className={styles.itemAge} aria-hidden={!isSelected}>
          {check}
        </span>
      )}
    </Ariakit.ComboboxItem>
  )
}

// #endregion
